/**
 * Canonical report catalog and access helpers.
 * Keep in sync with backend/constants/reportAccess.js
 */

export const REPORT_CATEGORIES = {
    product: 'Product Reports',
    customer: 'Customer Reports',
    financial: 'Financial Reports',
    inventory: 'Inventory Reports',
    supplier: 'Supplier Reports',
};

/** @type {{ key: string, label: string, category: keyof typeof REPORT_CATEGORIES, path: string }[]} */
export const REPORT_CATALOG = [
    { key: 'sales-report', label: 'Sales Report', category: 'product', path: '/reports/sales-report' },
    { key: 'scheme-report', label: 'Scheme Report', category: 'product', path: '/reports/scheme-report' },
    { key: 'sales-comparison', label: 'Sales Comparison', category: 'product', path: '/reports/sales-comparison' },
    { key: 'customer-statement', label: 'Customer Statement', category: 'customer', path: '/reports/customer-statement' },
    { key: 'distributor-statement', label: 'Distributor Statement', category: 'customer', path: '/reports/distributor-statement' },
    { key: 'distributor-balance-summary', label: 'Distributor Balance Summary', category: 'customer', path: '/reports/distributor-balance-summary' },
    { key: 'debtor-aging', label: 'Debtor Aging', category: 'customer', path: '/reports/debtor-aging' },
    { key: 'claim-statement', label: 'Claim Statement', category: 'customer', path: '/reports/claim-statement' },
    { key: 'customer-balance-summary', label: 'Customer Balance Summary', category: 'customer', path: '/reports/customer-balance-summary' },
    { key: 'trial-balance', label: 'Trial Balance', category: 'financial', path: '/reports/trial-balance' },
    { key: 'profit-loss', label: 'Profit & Loss', category: 'financial', path: '/reports/profit-loss' },
    { key: 'balance-sheet', label: 'Balance Sheet', category: 'financial', path: '/reports/balance-sheet' },
    { key: 'business-overview', label: 'Business Overview Report', category: 'financial', path: '/reports/monthly-closing' },
    { key: 'payment-register', label: 'Payment Register', category: 'financial', path: '/reports/payment-register' },
    { key: 'expense-register', label: 'Expense Report', category: 'financial', path: '/reports/expense-report' },
    { key: 'vendor-statement', label: 'Supplier Statement', category: 'supplier', path: '/reports/vendor-statement' },
    { key: 'inventory-movement', label: 'Inventory Movement', category: 'inventory', path: '/reports/inventory-movement' },
    { key: 'stock-quantity', label: 'Stock Quantity', category: 'inventory', path: '/reports/stock-quantity' },
    { key: 'stock-value', label: 'Stock Value', category: 'inventory', path: '/reports/stock-value' },
];

export const REPORT_KEYS = REPORT_CATALOG.map((r) => r.key);
const REPORT_KEY_SET = new Set(REPORT_KEYS);

const FULL_REPORT_ROLES = new Set([
    'Owner',
    'CompanyAdmin',
    'SaaSAdmin',
    'SoleDistributorAdmin',
    'SubDistributorAdmin',
]);

export function getReportByKey(reportKey) {
    return REPORT_CATALOG.find((r) => r.key === reportKey) || null;
}

export function getReportsByCategory(category) {
    return REPORT_CATALOG.filter((r) => r.category === category);
}

/**
 * Role-based report category access (mirrors Reports hub UX).
 */
export function roleAllowsReportCategory(role, category) {
    if (!role || !category) return false;
    if (FULL_REPORT_ROLES.has(role)) return true;

    if (['NSM', 'RSM', 'ZSM', 'ASM'].includes(role)) {
        return category === 'product' || category === 'customer';
    }
    if (role === 'WarehouseStaff' || role === 'StoreStaff') {
        return category === 'inventory';
    }
    if (role === 'AccountsStaff' || role === 'Accountant') {
        return ['product', 'customer', 'financial', 'inventory', 'supplier'].includes(category);
    }
    if (role === 'HRManager') {
        return category === 'financial' || category === 'customer';
    }
    return false;
}

export function getDefaultReportAccessForRole(role) {
    if (FULL_REPORT_ROLES.has(role) || role === 'SaaSAdmin') {
        return [...REPORT_KEYS];
    }
    return REPORT_CATALOG
        .filter((r) => roleAllowsReportCategory(role, r.category))
        .map((r) => r.key);
}

/**
 * Sanitize for form/API payloads.
 * null/undefined → null (role defaults); array → allowlist.
 */
export function sanitizeReportAccess(input) {
    if (input === null || input === undefined) return null;
    if (!Array.isArray(input)) return null;
    return [...new Set(input.map(String).filter((k) => REPORT_KEY_SET.has(k)))];
}

/**
 * Effective check: Owner/CompanyAdmin/SaaSAdmin always;
 * else if reportAccess is array use allowlist; else role defaults.
 * @param {object} user
 * @param {string} reportKey
 */
export function canUserAccessReport(user, reportKey) {
    if (!user || !reportKey || !REPORT_KEY_SET.has(reportKey)) return false;

    const role = user.role;
    if (role === 'Owner' || role === 'CompanyAdmin' || role === 'SaaSAdmin') return true;

    if (Array.isArray(user.reportAccess)) {
        return user.reportAccess.includes(reportKey);
    }

    if (FULL_REPORT_ROLES.has(role)) return true;

    const entry = getReportByKey(reportKey);
    if (!entry) return false;
    return roleAllowsReportCategory(role, entry.category);
}

/** Resolve path → report key for ProtectedRoute */
export function reportKeyFromPath(pathname) {
    if (!pathname) return null;
    const exact = REPORT_CATALOG.find((r) => r.path === pathname);
    if (exact) return exact.key;
    // Monthly closing / business overview share the same key
    if (pathname === '/reports/monthly-closing' || pathname === '/reports/business-overview') {
        return 'business-overview';
    }
    return null;
}
